// Libs
import React, { useEffect, useMemo, useState } from 'react'
// Styles
import './App.reset.scss'
import styles from './App.module.scss'
// Components
import Gallery from './components/Gallery'
import Footer from './components/Footer'
import ThemeToggle from './components/ThemeToggle'
// Site (owner customization) — composed into the base-library Footer container.
import Dock from './site/Dock'
// Utils
import { AppContext } from './App.context'
import { useScreenSize } from './App.hook'
import { siteConfig } from './config/site.config'

function App () {

  // Keep the document title in sync with config — a no-op in production (the
  // static <title> is already build-injected to the same value) but it fixes the
  // raw %REACT_APP_SITE_TITLE% placeholder shown in dev (where there's no build).
  useEffect(() => {
    if (siteConfig.htmlTitle) document.title = siteConfig.htmlTitle
  }, [])

  // Global
  const screenSize = useScreenSize()
  const isInitialized = useMemo(() => {
    const { width, height } = screenSize
    return !!(width && height)
  }, [screenSize])

  // When the lightbox opens, the top header (inside Gallery) and the bottom dock
  // (Footer, a sibling) both retreat off-frame so the enlarged photo owns the
  // stage. Gallery owns the lightbox state, so it reports the flag up here and we
  // hand it to the Footer.
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Language override (set from the debug panel; null = follow the browser).
  const [langOverride, setLangOverride] = useState<string | null>(null)

  // Context — memoized so unrelated App re-renders (e.g. the lightbox toggle
  // above) don't churn the value and re-render every context consumer.
  const contextValue = useMemo(() => ({
    // Flags
    isInitialized,
    // Global
    screenSize,
    // i18n
    lang: langOverride ?? (navigator.language || 'en'),
    langOverride,
    setLangOverride,
  }), [isInitialized, screenSize, langOverride])

  return (
    <AppContext.Provider value={contextValue}>
      <div className={styles.app}>
        <Gallery onLightboxChange={setLightboxOpen}/>
        <Footer hidden={lightboxOpen}><Dock /></Footer>
        <ThemeToggle hidden={lightboxOpen}/>
      </div>
    </AppContext.Provider>
  )
}

export default App
